import React, { useState, useEffect } from 'react';
import { 
  Button, 
  Input, 
  Modal, 
  Form, 
  Spin, 
  Table,
  Space,
  message,
  Popconfirm,
  Empty,
  Typography,
  ConfigProvider,
  Pagination
} from 'antd';
import { 
  FaRegTrashAlt, 
  FaEdit, 
  FaSearch, 
  FaPlus,
  FaUniversity 
} from 'react-icons/fa';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Api from '../api/Api';

const { Title } = Typography;

const GerenciarContasBancarias = () => {
  // Estados principais
  const [contas, setContas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  
  // Estados para modais
  const [isModalVisible, setIsModalVisible] = useState(false);
  
  // Estados para seleção
  const [selectedConta, setSelectedConta] = useState(null);
  
  // Formulário
  const [form] = Form.useForm();

  // Cores do degradê
  const primaryColor = 'rgb(249, 130, 68)';
  const secondaryColor = 'rgb(247, 72, 115)';
  const gradient = `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`;

  // Buscar dados iniciais
  useEffect(() => {
    fetchData();
  }, []);

    useEffect(() => {
     document.title = "Contas";
   
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const contasData = await Api.ListConta();
      
      setContas(contasData.map(c => ({ 
        ...c,
        key: c.id
      })));
    } catch (error) {
      toast.error('Erro ao carregar contas: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Formatar IBAN para exibição
  const formatIBAN = (iban) => {
    if (!iban) return '';
    // Formato: AO06 XXXX XXXX XXXX XXXX XXXX X
    return iban.replace(/(.{4})/g, '$1 ').trim();
  };

  // Validar e formatar IBAN durante a digitação
  const handleIbanChange = (e) => {
    let value = e.target.value.replace(/\s/g, '').toUpperCase();
    
    // Manter apenas letras e números
    value = value.replace(/[^A-Z0-9]/g, '');
    
    // Garantir que começa com AO06
    if (!value.startsWith('AO06')) {
      value = 'AO06' + value.substring(4);
    }
    
    // Limitar ao tamanho máximo do IBAN angolano (25 caracteres)
    if (value.length > 25) {
      value = value.substring(0, 25);
    }
    
    // Formatar com espaços a cada 4 caracteres
    const formattedValue = value.replace(/(.{4})/g, '$1 ').trim();
    
    // Atualizar o valor do campo
    form.setFieldsValue({ iban: formattedValue });
  };

  // Validar IBAN antes de enviar
  const validateIban = (_, value) => {
    if (!value) {
      return Promise.reject(new Error('IBAN é obrigatório'));
    }
    
    const cleanIban = value.replace(/\s/g, '');
    
    // Validar formato: AO06 + 21 caracteres = 25 caracteres no total
    const ibanRegex = /^AO06[A-Z0-9]{21}$/;
    
    if (!ibanRegex.test(cleanIban)) {
      return Promise.reject(new Error('IBAN deve ter o formato: AO06 XXXX XXXX XXXX XXXX XXXX X'));
    }
    
    return Promise.resolve();
  };

  // Filtrar contas
  const filteredContas = contas.filter(conta => {
    const searchLower = searchText.toLowerCase();
    return (
      conta.banco.toLowerCase().includes(searchLower) ||
      conta.iban.toLowerCase().includes(searchLower)
    );
  });

  // Paginação
  const paginatedContas = filteredContas.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  // Colunas da tabela
  const columns = [
    {
      title: 'Banco',
      dataIndex: 'banco',
      key: 'banco',
      sorter: (a, b) => a.banco.localeCompare(b.banco),
      render: (text) => (
        <Space>
          <FaUniversity style={{ color: primaryColor }} />
          <span>{text}</span>
        </Space>
      )
    },
    {
      title: 'IBAN',
      dataIndex: 'iban',
      key: 'iban',
      render: (text) => text
    },
    {
      title: 'Ações',
      key: 'acoes',
      width: 120,
      render: (_, record) => (
        <Space size="middle">
          <Button 
            icon={<FaEdit />}
            onClick={() => {
              setSelectedConta(record);
              form.setFieldsValue({
                banco: record.banco,
                iban:record.iban
              });
              setIsModalVisible(true);
            }}
            style={{
              color: primaryColor,
              borderColor: primaryColor,
              borderRadius: 6
            }}
          />
          
          <Popconfirm
            title="Excluir esta conta?"
            description="Tem certeza que deseja excluir esta conta bancária?"
            onConfirm={() => handleDelete(record.id)}
            okText="Sim"
            cancelText="Não"
            okButtonProps={{
              danger: true,
              style: { borderRadius: 6 }
            }}
            cancelButtonProps={{
              style: { borderRadius: 6 }
            }}
          >
            <Button 
              icon={<FaRegTrashAlt />}
              danger
              style={{ borderRadius: 6 }}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Manipuladores CRUD  
  const handleSubmit = async (values) => {
    try {
      setLoading(true);

      // Limpar espaços do IBAN antes de enviar
      const contaData = {
        ...values,
        iban: values.iban.replace(/\s/g, '')
      };

      if (selectedConta) {
        const response = await Api.AlterConta({ 
          id: selectedConta.id,
          ...contaData
        });
        
        if(response.success){
          fetchData();
          toast.success('Conta atualizada com sucesso!');
        } else if(response.message === 'Request failed with status code 409'){
          toast.error('Já existe uma conta com este IBAN.');
        } else {
          toast.error(response.message);
        }
      } else {
        const response = await Api.AddConta(contaData);
        
        if(response.success){
          fetchData();
          toast.success('Conta adicionada com sucesso!');
        } else if(response.message === 'Request failed with status code 409'){
          toast.error('Já existe uma conta com este IBAN.');
        } else {
          toast.error(response.message);
        }
      }

      setIsModalVisible(false);
      form.resetFields();
      setSelectedConta(null);
    } catch (error) {
      toast.error('Erro ao salvar conta: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      setLoading(true);
      const response = await Api.DeleteConta({ id });

      if(response.success){
        fetchData();
        toast.success('Conta excluída com sucesso!');
      } else if(response.message === 'Request failed with status code 409'){
        toast.error('Esta conta está vinculada a transações e não pode ser excluída.');
      } else {
        toast.error(response.message);
      }
      
      setCurrentPage(1);
    } catch (error) {
      toast.error('Erro ao excluir conta: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: primaryColor,
          borderRadius: 8,
        },
      }}
    >
      <div style={{ 
        padding: 24,
        background: '#f8f9fa',
        minHeight: '100vh'
      }}>
        <ToastContainer position="top-right" />
        
        {/* Cabeçalho */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          marginBottom: 24,
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 16
        }}>
          <Title level={2} style={{ 
            margin: 0,
            background: gradient,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            display: 'inline-block'
          }}>
            CONTAS BANCÁRIAS
          </Title>
          
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <Input
              placeholder="Pesquisar contas..."
              prefix={<FaSearch style={{ color: 'rgba(0,0,0,0.3)' }} />}
              value={searchText}
              onChange={(e) => {
                setSearchText(e.target.value);
                setCurrentPage(1);
              }}
              style={{ 
                width: 250,
                borderRadius: 8,
                border: '1px solid rgba(0,0,0,0.1)'
              }}
            />
            
            <Button 
              type="primary"
              icon={<FaPlus />}
              onClick={() => {
                setSelectedConta(null);
                form.resetFields();
                setIsModalVisible(true);
              }}
              style={{ 
                background: gradient,
                border: 'none',
                borderRadius: 8,
                boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
                height: 40,
                display: 'flex',
                alignItems: 'center'
              }}
            >
              Nova Conta
            </Button>
          </div>
        </div>

        {/* Tabela de Contas */}
        <Spin spinning={loading}>
          {filteredContas.length > 0 ? (
            <>
              <Table
                columns={columns}
                dataSource={paginatedContas}
                pagination={false}
                style={{
                  borderRadius: 8,
                  overflow: 'hidden',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                }}
                locale={{
                  emptyText: 'Nenhuma conta encontrada'
                }}
              />
              
              {/* Paginação */}
              <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                marginTop: 32,
                padding: '16px 0'
              }}>
                <Pagination
                  current={currentPage}
                  pageSize={pageSize}
                  total={filteredContas.length}
                  onChange={(page) => setCurrentPage(page)}
                  showSizeChanger={false}
                  style={{
                    borderRadius: 8,
                    overflow: 'hidden',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                  }}
                />
              </div>
            </>
          ) : (
            <Empty
              description={
                <span style={{ color: 'rgba(0,0,0,0.5)' }}>
                  Nenhuma conta encontrada {searchText ? 'com o filtro atual' : ''}
                </span>
              }
              style={{ 
                marginTop: 50,
                padding: 40,
                background: '#fff',
                borderRadius: 12,
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
              }}
            />
          )}
        </Spin>

        {/* Modal de edição/criação */}
        <Modal
          forceRender
          title={
            <span style={{
              background: gradient,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              display: 'inline-block'
            }}>
              {selectedConta ? 'Editar Conta' : 'Nova Conta'}
            </span>
          }
          open={isModalVisible}
          onCancel={() => {
            setIsModalVisible(false);
            form.resetFields();
            setSelectedConta(null);
          }}
          footer={[
            <Button 
              key="cancel" 
              onClick={() => {
                setIsModalVisible(false);
                form.resetFields();
                setSelectedConta(null);
              }}
              style={{ borderRadius: 6 }}
            >
              Cancelar
            </Button>,
            <Button 
              key="submit" 
              type="primary" 
              loading={loading}
              onClick={() => form.submit()}
              style={{ 
                background: gradient,
                border: 'none',
                borderRadius: 6,
                boxShadow: '0 2px 8px rgba(59, 130, 246, 0.3)'
              }}
            >
              {selectedConta ? 'Atualizar' : 'Salvar'}
            </Button>
          ]}
          width={500}
          destroyOnClose
          styles={{
            header: {
              borderBottom: '1px solid rgba(0,0,0,0.05)'
            },
            footer: {
              borderTop: '1px solid rgba(0,0,0,0.05)'
            }
          }}
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
          >
            <Form.Item
              name="banco"
              label="Nome do Banco"
              rules={[{ required: true, message: 'Campo obrigatório' }]}
            >
              <Input 
                placeholder="Digite o nome do banco"
                maxLength={100}
                style={{ borderRadius: 6 }}
              />
            </Form.Item>

            <Form.Item
              name="iban"
              label="IBAN"
              rules={[
                { required: true, message: 'Campo obrigatório' },
                { validator: validateIban }
              ]}
            >
              <Input 
                placeholder="AO06 XXXX XXXX XXXX XXXX XXXX X"
                onChange={handleIbanChange}
                maxLength={30} // Espaços incluídos
                style={{ borderRadius: 6, fontFamily: 'monospace' }}
              />
            </Form.Item>
            
            <div style={{ 
              padding: '12px 16px', 
              background: '#f0f7ff', 
              borderRadius: 6,
              border: '1px solid #d0e7ff',
              fontSize: '12px',
              color: '#0066cc'
            }}>
              <strong>Formato do IBAN:</strong> AO06 XXXX XXXX XXXX XXXX XXXX X
            </div>
          </Form>
        </Modal>
      </div>
    </ConfigProvider>
  );
};

export default GerenciarContasBancarias;